/**
 * lib/marketplaceEventLog.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin Metrics UX V1 — Marketplace Operating Dashboard.
 *
 * A dedicated, minimal, fire-and-forget logger that persists a handful of
 * marketplace funnel/engagement/error events to the backend (POST
 * /events/marketplace -> Supabase `marketplace_events`), independent of the
 * existing PostHog `track()` in lib/analytics.ts. Both are called ALONGSIDE
 * each other at the same call sites — this one exists because PostHog data
 * isn't queryable from /admin/metrics; see supabase/migrations/
 * 010_marketplace_events.sql for the full rationale.
 *
 * Privacy — enforced here, not just on the backend:
 *   - session_id is an opaque, locally-generated UUID — never an email,
 *     never linked to a Supabase Auth session.
 *   - utm_source/medium/campaign are read from the URL's own query params —
 *     never the free-text search query.
 *   - referrer_host is a bare hostname (document.referrer's own host),
 *     never the full referring URL (which could carry a search query typed
 *     on an earlier page).
 *   - landing_path is window.location.pathname only — never
 *     window.location.search (which could carry ?q=... free text) or hash.
 *   - Attribution (utm_ params, referrer_host, landing_path) is captured ONCE per
 *     browser tab (sessionStorage) at first touch, not re-derived on every
 *     event — a search results page's own path/params must never leak into
 *     "traffic source" fields on a later event fired from that same visit.
 */

import { API_BASE } from "@/lib/api";

const VISITOR_ID_KEY   = "sift_trip_visitor_id";
const ATTRIBUTION_KEY  = "sift_trip_attribution";

// ─── Anonymous visitor id ───────────────────────────────────────────────────

function getOrCreateVisitorId(): string | undefined {
  try {
    let id = localStorage.getItem(VISITOR_ID_KEY);
    if (!id) {
      id = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

// ─── Device type ────────────────────────────────────────────────────────────

function detectDeviceType(): "mobile" | "tablet" | "desktop" {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/ipad|tablet/i.test(ua)) return "tablet";
  if (/mobile|android|iphone|ipod/i.test(ua)) return "mobile";
  return "desktop";
}

// ─── First-touch attribution (captured once per tab) ────────────────────────

interface Attribution {
  utm_source?:    string;
  utm_medium?:    string;
  utm_campaign?:  string;
  referrer_host?: string;
  landing_path?:  string;
}

function captureAttributionOnce(): Attribution {
  try {
    const cached = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (cached) return JSON.parse(cached) as Attribution;
  } catch { /* ignore — fall through and recompute */ }

  let referrerHost: string | undefined;
  try {
    referrerHost = document.referrer ? new URL(document.referrer).hostname : undefined;
  } catch {
    referrerHost = undefined;
  }

  const params = new URLSearchParams(window.location.search);
  const attribution: Attribution = {
    utm_source:    params.get("utm_source")   || undefined,
    utm_medium:    params.get("utm_medium")   || undefined,
    utm_campaign:  params.get("utm_campaign") || undefined,
    referrer_host: referrerHost,
    landing_path:  window.location.pathname || undefined,
  };

  try { sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution)); } catch { /* ignore */ }
  return attribution;
}

// ─── Duration bucketing — bucketed, never a raw millisecond value ──────────

export function bucketDurationSeconds(seconds: number): string {
  if (seconds < 10)  return "0-10s";
  if (seconds < 30)  return "10-30s";
  if (seconds < 60)  return "30-60s";
  if (seconds < 120) return "60-120s";
  return "120s+";
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Sprint: Product Analytics V1 — declared once as a runtime array (not just
 * a TS union) so the allowlist can be enforced at RUNTIME, not just at
 * compile time. TS types are erased on build — a caller that reaches this
 * function via `as` / plain JS (a stray copy-paste, a future non-TS
 * caller) would otherwise sail straight past the type system and still hit
 * the network with an unlisted event name. The backend re-validates this
 * exact same allowlist independently (main.py's _MARKETPLACE_EVENT_NAMES)
 * — this is defense in depth, never the only gate.
 */
export const MARKETPLACE_LOGGED_EVENT_NAMES = [
  "experiences_search_submitted",
  "marketplace_results_loaded",
  "marketplace_detail_opened",
  "marketplace_compare_item_added",
  "marketplace_comparison_opened",
  "marketplace_provider_clicked",
  "marketplace_page_engagement",
  "marketplace_panel_error",
  "intent_parser_fallback",
  // Sprint: Product Analytics V1
  "marketplace_filter_applied",
  "wishlist_item_saved",
  "wishlist_item_removed",
  "comparison_saved",
  "currency_changed",
  "language_changed",
  "auth_started",
  "auth_completed",
  "account_tab_opened",
] as const;

export type MarketplaceLoggedEventName = typeof MARKETPLACE_LOGGED_EVENT_NAMES[number];

const _EVENT_NAME_SET: ReadonlySet<string> = new Set(MARKETPLACE_LOGGED_EVENT_NAMES);

export interface MarketplaceEventPayload {
  destination_iata?:        string | null;
  provider_name?:           string | null;
  category?:                string | null;
  source_class?:            string | null;
  surface?:                 string | null;
  duration_seconds_bucket?: string | null;
  had_results?:             boolean | null;
  provider_clicked?:        boolean | null;
  comparison_opened?:       boolean | null;
  // Provider Landing Accuracy + Query Expansion Prep V1 — never booking_url
  // itself, only whether one existed and which shape fed the anchor href.
  has_booking_url?:         boolean | null;
  link_variant?:            string | null;
  // ── Sprint: Product Analytics V1 ──────────────────────────────────────────
  // comparison_saved — the DB column (010/014) already existed; no
  // backend writer ever set it until now.
  comparison_size?:          number | null;
  // marketplace_filter_applied — filter_value is a resolved UI value (a
  // category tag, provider slug, taxonomy id, a price threshold, a
  // "true"/"false" toggle string) — never the free-text search query.
  filter_type?:              string | null;
  filter_value?:             string | null;
  active_filter_count?:      number | null;
  result_count_after?:       number | null;
  // currency_changed / language_changed — app PREFERENCES only.
  language?:                 string | null;
  currency?:                 string | null;
  // auth_started / auth_completed — WHICH method, WHETHER it succeeded.
  // Never an email, name, or Supabase user id.
  auth_method?:              string | null;
  success?:                  boolean | null;
}

// ─── Free-text scrubber (task 7 — query privacy) ───────────────────────────
// No current call site passes raw free text into logMarketplaceEvent (every
// field above is either a coarse enum-like label or a number/boolean) — this
// exists as the documented, testable, reusable primitive the brief asks
// for, and as the actual sanitizing pass every string field gets before
// the request body is built (see _sanitizePayload below), so a future call
// site that accidentally passes something user-typed is scrubbed rather
// than trusted. Order matters: URLs first (a URL can itself contain an
// email-shaped or digit-shaped substring in its path/query), then emails,
// then long digit runs (phone numbers), then whitespace/length.
const _URL_RE   = /\bhttps?:\/\/\S+/gi;
const _EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
// 8+ digits, optionally interleaved with spaces/parens/dashes/dots — a
// phone number shape. Deliberately NOT applied to session_id (an opaque
// UUID, which legitimately contains long pure-digit runs) — see
// _NEVER_SCRUB_KEYS below.
const _PHONE_RE = /\+?\d[\d\s().-]{7,}\d/g;

export function scrubFreeText(value: string, maxLen = 60): string {
  let v = value;
  v = v.replace(_URL_RE, " ");
  v = v.replace(_EMAIL_RE, " ");
  v = v.replace(_PHONE_RE, " ");
  v = v.replace(/\s+/g, " ").trim();
  return v.slice(0, maxLen);
}

// Fields that are always an opaque identifier, never user-facing text —
// scrubbing them would corrupt a legitimate UUID's digit runs, not protect
// privacy (the id is already opaque, never itself PII).
const _NEVER_SCRUB_KEYS = new Set<string>(["session_id"]);

function _sanitizePayload(payload: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    out[key] = (typeof value === "string" && !_NEVER_SCRUB_KEYS.has(key))
      ? scrubFreeText(value, 120)
      : value;
  }
  return out;
}

/**
 * Fire-and-forget — never throws, never blocks the caller, never retried.
 * `keepalive: true` lets the request complete even if the page is
 * navigating away right after the call (provider clicks, page-hide
 * engagement pings) — same purpose as navigator.sendBeacon, via fetch.
 * Sprint: Product Analytics V1 — an event_name outside the allowlist is
 * dropped here, client-side, before any network call (see
 * MARKETPLACE_LOGGED_EVENT_NAMES above); every payload also goes through
 * _sanitizePayload (task 4's own "validate/clean props antes de enviar").
 */
export function logMarketplaceEvent(
  eventName: MarketplaceLoggedEventName,
  payload: MarketplaceEventPayload = {},
): void {
  if (!_EVENT_NAME_SET.has(eventName)) return;
  _observeForEngagement(eventName, payload);
  try {
    const body = JSON.stringify(_sanitizePayload({
      event_name:  eventName,
      session_id:  getOrCreateVisitorId(),
      device_type: detectDeviceType(),
      ...captureAttributionOnce(),
      ...payload,
    }));
    void fetch(`${API_BASE}/events/marketplace`, {
      method:    "POST",
      headers:   { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => { /* silent — logging must never surface as a UX error */ });
  } catch {
    /* silent */
  }
}

// ─── Page engagement — module-level state, updated automatically ───────────
// Cross-component by design: provider clicks/comparison opens fire from
// several far-apart components (ComparisonDrawer, FeaturedShelf,
// ExperienceDetailDrawer, ExperienceMarketplaceRow), all of which already
// call logMarketplaceEvent() above — piggybacking here avoids prop-drilling
// or a React context just to answer "did this page view see a click".

let _pageMountedAt:         number  = 0;
let _pageHadResults:        boolean = false;
let _pageProviderClicked:   boolean = false;
let _pageComparisonOpened:  boolean = false;
let _pageDestinationIata:   string | null | undefined;
let _engagementFired:       boolean = true; // starts "already fired" until initPageEngagementTracking() runs

function _now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function _observeForEngagement(eventName: MarketplaceLoggedEventName, payload: MarketplaceEventPayload): void {
  if (eventName === "marketplace_results_loaded" && payload.had_results) _pageHadResults = true;
  if (eventName === "marketplace_provider_clicked")  _pageProviderClicked  = true;
  if (eventName === "marketplace_comparison_opened") _pageComparisonOpened = true;
  if (payload.destination_iata) _pageDestinationIata = payload.destination_iata;
}

/**
 * Call once per route mount (e.g. `useEffect(() => initPageEngagementTracking(), [])`
 * in the Experiences route). Resets the tracked state for this page view,
 * fires `marketplace_page_engagement` on tab hide / page unload / when the
 * returned cleanup runs (route change or component unmount) — whichever
 * happens first, exactly once. Never a raw millisecond value — always a
 * bucket (see bucketDurationSeconds).
 */
export function initPageEngagementTracking(): () => void {
  _pageMountedAt        = _now();
  _pageHadResults       = false;
  _pageProviderClicked  = false;
  _pageComparisonOpened = false;
  _pageDestinationIata  = undefined;
  _engagementFired      = false;

  const fireEngagement = () => {
    if (_engagementFired) return;
    _engagementFired = true;
    const durationSeconds = Math.max(0, (_now() - _pageMountedAt) / 1000);
    logMarketplaceEvent("marketplace_page_engagement", {
      destination_iata:        _pageDestinationIata,
      duration_seconds_bucket: bucketDurationSeconds(durationSeconds),
      had_results:             _pageHadResults,
      provider_clicked:        _pageProviderClicked,
      comparison_opened:       _pageComparisonOpened,
    });
  };

  const handleVisibility = () => {
    if (document.visibilityState === "hidden") fireEngagement();
  };

  if (typeof document !== "undefined") document.addEventListener("visibilitychange", handleVisibility);
  if (typeof window   !== "undefined") window.addEventListener("pagehide", fireEngagement);

  return () => {
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", handleVisibility);
    if (typeof window   !== "undefined") window.removeEventListener("pagehide", fireEngagement);
    fireEngagement();
  };
}
